import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useSwapSimCard } from '@/hooks/useLineActions';
import { useSimCards } from '@/hooks/useSimCards';
import { createLogger } from '@/lib/logger';
import {
  AlertCircle,
  Check,
  CreditCard,
  Loader2,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const log = createLogger('SwapSimCardDialog');

export function SwapSimCardDialog({
  isOpen,
  onClose,
  lineId,
  operatorName,
}: {
  isOpen: boolean;
  onClose: () => void;
  lineId: string;
  operatorName: string;
}) {
  const [simCardType, setSimCardType] = useState<'basic' | 'esim'>('basic');
  const [selectedSimCardId, setSelectedSimCardId] = useState('');
  const { mutate: swapSimCard, isPending } = useSwapSimCard();
  const { data: freeSimCards = [], isLoading: isLoadingSimCards } =
    useSimCards(operatorName);

  const handleSubmit = () => {
    swapSimCard(
      {
        lineId,
        simCardType,
        simCardId: simCardType === 'basic' ? selectedSimCardId : undefined,
      },
      {
        onSuccess: () => {
          onClose();
          toast.success('Carte SIM changée avec succès');
        },
        onError: (error) => {
          log.error('Error swapping SIM card', error);
          toast.error(
            `Erreur lors du changement de carte SIM: ${
              error.message || 'Erreur inconnue'
            }`
          );
        },
      }
    );
  };

  const availableSimCards = freeSimCards.filter(
    (sim) => sim.status === 'free' && sim.type === 'basic'
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Changer de carte SIM</DialogTitle>
          <DialogDescription>
            Choisissez le type de carte SIM vers laquelle vous souhaitez
            changer.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-4 overflow-y-auto flex-1 min-h-0">
          <RadioGroup
            value={simCardType}
            onValueChange={(value) => {
              setSimCardType(value as 'basic' | 'esim');
              setSelectedSimCardId('');
            }}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="basic" id="basic" />
              <Label htmlFor="basic" className="cursor-pointer">
                SIM physique
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="esim" id="esim" />
              <Label htmlFor="esim" className="cursor-pointer">
                eSIM
              </Label>
            </div>
          </RadioGroup>

          {simCardType === 'basic' && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">
                  Sélectionner une carte SIM disponible
                </Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Choisissez une carte SIM physique disponible pour cette ligne.
                </p>
              </div>
              <Command
                className="rounded-lg border overflow-hidden max-h-[240px]"
                filter={(value, search) => {
                  if (!search) return 1;
                  const normalizedValue = value.toLowerCase();
                  const normalizedSearch = search.toLowerCase();
                  return normalizedValue.includes(normalizedSearch) ? 1 : 0;
                }}
              >
                <CommandInput
                  placeholder="Rechercher par numéro de série ou opérateur..."
                  className="h-11"
                />
                <CommandList>
                  <CommandEmpty>
                    {isLoadingSimCards ? (
                      <div className="py-6 text-center">
                        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                        <p className="mt-2 text-sm text-muted-foreground">
                          Chargement des cartes SIM...
                        </p>
                      </div>
                    ) : availableSimCards.length === 0 ? (
                      <div className="flex flex-col items-center py-6 gap-3">
                        <p className="text-sm text-muted-foreground">
                          Aucune carte SIM disponible
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center py-6 gap-3">
                        <p className="text-sm text-muted-foreground">
                          Aucune carte SIM trouvée
                        </p>
                      </div>
                    )}
                  </CommandEmpty>
                  <CommandGroup className="max-h-[200px] overflow-auto">
                    {availableSimCards.map((simCard) => (
                      <CommandItem
                        key={simCard.id}
                        value={`${simCard.serialNumber || ''} ${simCard.operatorLabel || ''}`.trim()}
                        onSelect={() => {
                          setSelectedSimCardId(simCard.id);
                        }}
                        className={`py-3 ${
                          selectedSimCardId === simCard.id
                            ? 'bg-primary/10 font-medium'
                            : ''
                        }`}
                      >
                        <div className="flex items-center gap-3 w-full">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <CreditCard className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex flex-col min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate">
                                {simCard.serialNumber || 'N/A'}
                              </span>
                              {selectedSimCardId === simCard.id && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-primary/15 text-primary font-medium">
                                  Sélectionné
                                </span>
                              )}
                            </div>
                            {simCard.operatorLabel && (
                              <span className="text-xs text-muted-foreground truncate">
                                {simCard.operatorLabel}
                              </span>
                            )}
                          </div>
                          {selectedSimCardId === simCard.id && (
                            <Check className="ml-auto h-4 w-4 text-primary" />
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>

              {selectedSimCardId && (
                <div className="flex items-center gap-2 p-4 rounded-lg border bg-primary/10 shadow-sm">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex flex-col flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {
                          availableSimCards.find(
                            (s) => s.id === selectedSimCardId
                          )?.serialNumber
                        }
                      </span>
                      <span className="px-2 py-0.5 text-xs rounded-full bg-primary/15 text-primary font-medium">
                        Sélectionné
                      </span>
                    </div>
                    {availableSimCards.find((s) => s.id === selectedSimCardId)
                      ?.operatorLabel && (
                      <span className="text-sm text-muted-foreground">
                        {
                          availableSimCards.find(
                            (s) => s.id === selectedSimCardId
                          )?.operatorLabel
                        }
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-auto"
                    onClick={() => setSelectedSimCardId('')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {simCardType === 'esim' && (
            <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-blue-900">
                    Coût de l'eSIM : 3€
                  </p>
                  <p className="text-sm text-blue-800">
                    La durée de création de la e-SIM est d'environ 5 minutes.
                    Une fois créée, un QR code apparaîtra sur cette page,
                    celui-ci pourra directement être flashé avec un téléphone ou
                    envoyé par mail. Le code PIN par défaut est 0000.
                  </p>
                  <p className="text-sm font-bold text-red-600">
                    Attention : une e-SIM ne peut être flashée qu'une seule
                    fois.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              isPending || (simCardType === 'basic' && !selectedSimCardId)
            }
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Changement en cours...
              </>
            ) : (
              'Confirmer le changement'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
